import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import CyberCubLogo from '../../components/CyberCubLogo.jsx';
import Tutor from '../../components/Tutor.jsx';
import { useFinished } from '../../lib/practical.js';

/**
 * Practical site for "How Cyberattacks Actually Happen".
 *
 * The lesson says recon is mostly just reading. This is that, done:
 * a small public website, a job advert, a news post and a photograph,
 * with seven findings buried in them that a stranger could use.
 *
 * Nothing here is an attack and nothing here is even slightly clever.
 * Every finding is something the organisation published on purpose,
 * which is exactly the discomfort worth producing - you cannot patch
 * your way out of a careers page.
 *
 * The second half maps what was found onto the chain from the lesson,
 * so the point lands: the reading was stage one of something, and the
 * cheapest place to break the chain was before any of it happened.
 */

const STORE_KEY = 'cybercub.lab.recon';

/* ------------------------------------------------------------------ */
/* the target                                                         */
/* ------------------------------------------------------------------ */

const PAGES = [
  {
    id: 'home',
    tab: 'Home',
    body: [
      { kind: 'h', text: 'Marbury Tutors' },
      { kind: 'p', text: 'One-to-one tutoring across Marbury and online, since 2014. Book a free first session through our booking site.' },
      { kind: 'p', text: 'Office: Unit 4, Marbury Business Park. Open Mon–Fri.' },
    ],
  },
  {
    id: 'team',
    tab: 'Our team',
    body: [
      { kind: 'h', text: 'Our team' },
      {
        kind: 'person',
        find: 'emails',
        name: 'Rachel Okonkwo',
        role: 'Operations Director',
        line: 'r.okonkwo@marburytutors.co.uk',
      },
      {
        kind: 'person',
        find: 'emails',
        name: 'Tom Bright',
        role: 'Office Manager',
        line: 't.bright@marburytutors.co.uk',
      },
      {
        kind: 'person',
        find: 'newstarter',
        name: 'Priya Nair',
        role: 'Office Assistant — started this month',
        line: 'p.nair@marburytutors.co.uk',
      },
    ],
  },
  {
    id: 'jobs',
    tab: 'Work with us',
    body: [
      { kind: 'h', text: 'IT Support Technician' },
      {
        kind: 'p',
        find: 'software',
        text: 'You will look after our Windows Server 2012 file server and the Exchange 2013 mailboxes, alongside around 30 desktops.',
      },
      {
        kind: 'p',
        find: 'vpn',
        text: 'Experience with our remote access setup (SonicWall, currently unmanaged) would be an advantage.',
      },
      { kind: 'p', text: 'Salary on application. To apply, email your CV to the office manager.' },
    ],
  },
  {
    id: 'news',
    tab: 'News',
    body: [
      { kind: 'h', text: 'A new home for our bookings' },
      {
        kind: 'p',
        find: 'supplier',
        text: 'We have moved our bookings to Clearpay Systems, who now handle all card payments for us. Nothing changes for parents.',
      },
      {
        kind: 'p',
        find: 'holiday',
        text: 'The office will be closed for the last week of October — Rachel and Tom are both away, so please expect slower replies.',
      },
    ],
  },
  {
    id: 'photo',
    tab: 'Photo',
    body: [
      { kind: 'h', text: 'Posted to social media: "New office, first day!"' },
      { kind: 'photo' },
    ],
  },
];

/*
 * What a stranger can take away, and what it is for. `where` names the
 * tab it is on, so a learner who has missed one knows where to look.
 */
const FINDINGS = [
  {
    id: 'emails',
    icon: '📧',
    label: 'The email pattern is first initial, dot, surname',
    where: 'Our team',
    worth:
      'Two addresses are enough to guess every other one in the company. Nobody published a list; they published a rule.',
  },
  {
    id: 'newstarter',
    icon: '🐣',
    label: 'Priya started this month',
    where: 'Our team',
    worth:
      'A new starter does not yet know who normally emails her, what the finance process is, or that the director never asks for things this way. She is the easiest person here to fool, and the website says so.',
  },
  {
    id: 'software',
    icon: '🖥️',
    label: 'Windows Server 2012 and Exchange 2013',
    where: 'Work with us',
    worth:
      'Both are long out of support, so the holes in them are public and will never be fixed. The job advert is an inventory, and it was written to be helpful.',
  },
  {
    id: 'vpn',
    icon: '🔌',
    label: 'The remote access box is "currently unmanaged"',
    where: 'Work with us',
    worth:
      'Unmanaged means nobody is updating it. It is the one device deliberately exposed to the internet, and they have said in public that it is unattended.',
  },
  {
    id: 'supplier',
    icon: '🤝',
    label: 'Clearpay Systems handles their payments',
    where: 'News',
    worth:
      'Now there are two ways in. An email that appears to come from Clearpay will be believed here, and a weakness at Clearpay becomes a weakness here.',
  },
  {
    id: 'holiday',
    icon: '🌴',
    label: 'Both senior staff are away the last week of October',
    where: 'News',
    worth:
      'A request that would normally be checked with Rachel cannot be. Attacks are timed, and the timing was published.',
  },
  {
    id: 'badge',
    icon: '🪪',
    label: 'A door pass is readable in the photograph',
    where: 'Photo',
    worth:
      'Enough to copy the look of it, and enough to know what the building expects to see. Nobody photographs the pass on purpose; it is just round somebody’s neck.',
  },
];

/* Where the pass sits on the drawn photo, in percentages. */
const BADGE_SPOT = { x: 41, y: 47, w: 13, h: 20 };

/* ------------------------------------------------------------------ */
/* the chain                                                          */
/* ------------------------------------------------------------------ */

const CHAIN = [
  {
    id: 'step1',
    stage: 'Reconnaissance',
    text: 'Read everything published. Names, addresses, software, suppliers, dates.',
    note: 'You have just done this, and not one thing you did was an attack.',
  },
  {
    id: 'step2',
    stage: 'Choosing a way in',
    text: 'Weigh the unpatched server against the unmanaged remote box against Priya.',
    note: 'The people are usually cheaper than the machines, which is why the next lesson is about people.',
  },
  {
    id: 'step3',
    stage: 'The approach',
    text: 'An email from “Clearpay Systems” to p.nair@, in the week Rachel and Tom are away.',
    note: 'Every detail there came from the website. None of it was guessed.',
  },
  {
    id: 'step4',
    stage: 'Getting a foothold',
    text: 'One password typed into one convincing page.',
    note: 'This is the first moment anything technical happens — and the first moment most defences start looking.',
  },
  {
    id: 'step5',
    stage: 'Spreading and staying',
    text: 'From the mailbox to the file server that has not been patched since 2012.',
    note: 'By now it is far too late to be cheap to fix.',
  },
];

/* ------------------------------------------------------------------ */

const load = () => {
  try { return JSON.parse(localStorage.getItem(STORE_KEY) ?? '{}'); } catch { return {}; }
};
const save = (v) => {
  try { localStorage.setItem(STORE_KEY, JSON.stringify(v)); } catch { /* private window */ }
};

export default function ReconLab() {
  const saved = useMemo(load, []);

  const [tab, setTab] = useState('site');
  const [page, setPage] = useState('home');
  const [found, setFound] = useState(saved.found ?? []);

  useEffect(() => { document.title = 'Recon on a real target · CyberCub Lab'; }, []);
  useEffect(() => { save({ found }); }, [found]);

  const take = (id) => {
    if (!id || found.includes(id)) return;
    setFound((f) => [...f, id]);
  };

  const allFound = found.length === FINDINGS.length;

  /* Finding all seven only unlocks the chain; reading the chain is the
     half that explains what they were for. */
  useFinished('/lab/recon', allFound && tab === 'chain');

  const current = PAGES.find((p) => p.id === page);

  const TABS = [
    { id: 'site', n: 1, label: 'Read what they published', locked: false },
    { id: 'chain', n: 2, label: 'Where this was going', locked: !allFound },
  ];

  return (
    <div className="lab">
      <header className="lab-top">
        <div className="row gap-sm">
          <CyberCubLogo size={38} />
          <div>
            <strong>Recon on a real target</strong>
            <span className="lab-sub">Practical · Ethical Hacker, Module 1</span>
          </div>
        </div>
        <Link to="/learn/lesson/eh1l3-how-attacks-happen" className="btn btn-sm btn-ghost">
          ← Back to the lesson
        </Link>
      </header>

      <nav className="lab-tabs" aria-label="Sections">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`lab-tab ${tab === t.id ? 'is-active' : ''} ${t.locked ? 'is-locked' : ''}`}
            onClick={() => !t.locked && setTab(t.id)}
            disabled={t.locked}
            aria-current={tab === t.id ? 'page' : undefined}
          >
            <span className="lab-tab-n">{t.n}</span>
            {t.label}
            {t.locked && <span aria-label="locked"> 🔒</span>}
          </button>
        ))}
      </nav>

      {/* ---------------- 1. the site ---------------- */}
      {tab === 'site' && (
        <section className="lab-section">
          <div className="place-head">
            <div>
              <h2>Read what they published</h2>
              <p className="lab-lead">
                Everything here is on the public internet and was put there on purpose. Click
                anything a stranger could use. Seven things are worth taking.
              </p>
            </div>
            <span className="pill">{found.length} / {FINDINGS.length} found</span>
          </div>

          <Tutor
            avatar="cub-grape"
            say={
              allFound
                ? 'Seven, and you have not touched a single one of their systems. That is what makes this stage so hard to defend: there is nothing to patch, because nothing went wrong. Somebody wrote a helpful job advert.'
                : `Read like a stranger rather than a customer. Not "what does this company do" but "what does this tell me about how to get in". ${
                    found.length ? `${FINDINGS.length - found.length} left.` : 'Try the team page first.'
                  }`
            }
          />

          {/* the browser */}
          <div className="recon-browser">
            <div className="recon-chrome">
              <span className="recon-dot" /><span className="recon-dot" /><span className="recon-dot" />
              <span className="recon-url">https://www.marburytutors.co.uk</span>
            </div>

            <nav className="recon-nav" aria-label="Pages">
              {PAGES.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className={`recon-tab ${page === p.id ? 'is-active' : ''}`}
                  onClick={() => setPage(p.id)}
                >
                  {p.tab}
                </button>
              ))}
            </nav>

            <div className="recon-page">
              {current.body.map((b, i) => {
                if (b.kind === 'h') return <h3 key={i} className="recon-h">{b.text}</h3>;

                if (b.kind === 'person') {
                  const got = found.includes(b.find);
                  return (
                    <button
                      key={i}
                      type="button"
                      className={`recon-person ${got ? 'is-found' : ''}`}
                      onClick={() => take(b.find)}
                    >
                      <strong>{b.name}</strong>
                      <em>{b.role}</em>
                      <code>{b.line}</code>
                    </button>
                  );
                }

                if (b.kind === 'photo') {
                  const got = found.includes('badge');
                  return (
                    <div key={i} className="recon-photo">
                      <svg viewBox="0 0 100 62" aria-label="A photograph of two people at a desk">
                        <rect className="ph-wall" x="0" y="0" width="100" height="62" />
                        <rect className="ph-window" x="62" y="6" width="30" height="22" rx="1" />
                        <rect className="ph-desk" x="6" y="44" width="88" height="4" rx="1" />
                        <rect className="ph-screen" x="58" y="30" width="22" height="14" rx="1" />
                        {/* two people */}
                        <circle className="ph-skin" cx="30" cy="22" r="6" />
                        <path className="ph-top" d="M20,44 q10,-16 20,0 z" />
                        <circle className="ph-skin" cx="48" cy="24" r="5.4" />
                        <path className="ph-top is-two" d="M39,44 q9,-15 18,0 z" />
                        {/* the lanyard and pass */}
                        <path className="ph-lanyard" d="M45,29 L47.5,37 M51,29 L48.5,37" />
                        <rect className="ph-badge" x="44.6" y="36.4" width="8" height="10" rx="0.8" />
                        <rect className="ph-badge-strip" x="44.6" y="36.4" width="8" height="2.4" rx="0.8" />
                        <circle className="ph-badge-face" cx="48.6" cy="41" r="1.6" />
                        <rect className="ph-badge-line" x="45.6" y="43.6" width="6" height="0.7" rx="0.35" />
                      </svg>

                      <button
                        type="button"
                        className={`recon-spot ${got ? 'is-found' : ''}`}
                        style={{
                          left: `${BADGE_SPOT.x}%`, top: `${BADGE_SPOT.y}%`,
                          width: `${BADGE_SPOT.w}%`, height: `${BADGE_SPOT.h}%`,
                        }}
                        onClick={() => take('badge')}
                        aria-label={got ? 'Door pass, found' : 'Something in the photograph'}
                      />
                      <p className="recon-caption">
                        Nobody posted this to give anything away. They posted it because it was
                        somebody’s first day.
                      </p>
                    </div>
                  );
                }

                const got = b.find && found.includes(b.find);
                return b.find ? (
                  <button
                    key={i}
                    type="button"
                    className={`recon-line ${got ? 'is-found' : ''}`}
                    onClick={() => take(b.find)}
                  >
                    {b.text}
                  </button>
                ) : (
                  <p key={i} className="recon-p">{b.text}</p>
                );
              })}
            </div>
          </div>

          {/* the notebook */}
          <div className="recon-notes">
            <h3>Your notes</h3>
            {found.length === 0 ? (
              <p className="recon-empty">Nothing yet. Click anything a stranger could use.</p>
            ) : (
              <ul>
                {FINDINGS.filter((f) => found.includes(f.id)).map((f) => (
                  <li key={f.id}>
                    <span aria-hidden="true">{f.icon}</span>
                    <div>
                      <strong>{f.label}</strong>
                      <p>{f.worth}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}

            {!allFound && found.length > 0 && (
              <p className="recon-hint">
                Still missing:{' '}
                {[...new Set(FINDINGS.filter((f) => !found.includes(f.id)).map((f) => f.where))].join(', ')}.
              </p>
            )}
          </div>

          {allFound && (
            <div className="lab-done">
              <div>
                <strong>Seven findings, nothing touched.</strong>
                <p style={{ margin: '0.3rem 0 0' }}>
                  Now see what they were for.
                </p>
              </div>
              <button type="button" className="btn btn-sm" onClick={() => setTab('chain')}>
                Where this was going →
              </button>
            </div>
          )}
        </section>
      )}

      {/* ---------------- 2. the chain ---------------- */}
      {tab === 'chain' && (
        <section className="lab-section">
          <div className="place-head">
            <div>
              <h2>Where this was going</h2>
              <p className="lab-lead">
                Every stage below is built out of something you read on their own website. The
                attack has not started yet, and it is already most of the way planned.
              </p>
            </div>
            <button type="button" className="btn btn-sm btn-ghost" onClick={() => setTab('site')}>
              ← Back to the site
            </button>
          </div>

          <Tutor
            avatar="cub-coral"
            say={
              'Look at where it becomes technical — stage four. Everything before that is reading and writing, and every defence the school bought is watching stage four and later. That gap is why recon is worth understanding: it is the part nobody is looking at, and the part you can still do something about.'
            }
          />

          <ol className="chain">
            {CHAIN.map((c, i) => (
              <li key={c.id} className={i === 3 ? 'is-pivot' : ''}>
                <span className="chain-n">{i + 1}</span>
                <div>
                  <strong>{c.stage}</strong>
                  <p>{c.text}</p>
                  <em>{c.note}</em>
                </div>
              </li>
            ))}
          </ol>

          <div className="lab-done">
            <div>
              <strong>None of stage one was illegal, clever or preventable by software.</strong>
              <p style={{ margin: '0.3rem 0 0' }}>
                Which does not mean nothing can be done. The job advert did not need version
                numbers. The news post did not need the dates both directors were away. The photo
                did not need the pass in it. You cannot patch a careers page — but you can write it
                differently.
              </p>
            </div>
            <Link to="/learn/lesson/eh1l3-how-attacks-happen" className="btn btn-sm">
              ← Back to the lesson
            </Link>
          </div>
        </section>
      )}
    </div>
  );
}
